/** Parent detail collapse. */
import * as React from 'react';
import { CheckCircle2, User } from 'lucide-react';
import { LabelBadge } from '@/components/common/LabelBadge';
import { Tabs, type TabItem } from '@/components/ui/tabs';
import {
  getParentBusinessStatus,
  PARENT_PENDING_STATUS_CONFIG,
  PARENT_ACTIVE_STATUS_CONFIG,
  PARENT_DETAIL_TAB_CONFIGS,
  type ParentDetailTab,
  REGISTRATION_SOURCE_CONFIG,
  DEFAULT_SOURCE_CONFIG,
} from '@/components/parents/parent-configs';
import { useGetParentRemoteChildren } from '@/hooks/useUsers';
import { useProgramsForMode } from '@/hooks/useProgramsForMode';
import { useAuthStore } from '@/stores/authStore';
import { useDepartments } from '@/hooks/useDepartments';
import type { FullState, ParentRecord } from '@/types/parent.types';
import { UserHistoryTimeline } from '@/components/common/details/UserHistoryTimeline';
import { ParentDetailsTab } from './parent-detail-tabs/ParentDetailsTab';
import { ParentRegistrationTab } from './parent-detail-tabs/ParentRegistrationTab';
import { ParentChildrenTab } from './parent-detail-tabs/ParentChildrenTab';

/** Props for {@link ParentDetails}. */
interface Props {
  record: ParentRecord;
  /** Full-state extras (templates / steps) when the parent already fetched GET full-state. */
  fullState?: FullState;
}

/** Parent detail collapse. */
export const ParentDetails: React.FC<Props> = ({ record, fullState }) => {
  const { user } = useAuthStore();
  const [activeTab, setActiveTab] = React.useState<ParentDetailTab>('details');

  const { data: programsData } = useProgramsForMode();
  const programs = programsData?.data ?? [];

  const { data: children = [], isLoading: isLoadingChildren } = useGetParentRemoteChildren(
    record.userId,
    user?.companyId ?? '',
    { enabled: activeTab === 'children' && !!user?.companyId },
  );

  const { data: departments = [] } = useDepartments('FR');

  const detailRecord = record;

  const { tab, status } = getParentBusinessStatus({
    onboardingStatus: detailRecord.onboardingStatus,
    folderSituation: detailRecord.folderSituation,
    user: detailRecord.user,
    _childrenCount: (detailRecord as { _childrenCount?: number })._childrenCount,
  });

  let statusConfig:
    | (typeof PARENT_PENDING_STATUS_CONFIG)[keyof typeof PARENT_PENDING_STATUS_CONFIG]
    | (typeof PARENT_ACTIVE_STATUS_CONFIG)[keyof typeof PARENT_ACTIVE_STATUS_CONFIG]
    | undefined;
  if (tab === 'pre_registered') {
    statusConfig =
      PARENT_PENDING_STATUS_CONFIG[status as keyof typeof PARENT_PENDING_STATUS_CONFIG];
  } else {
    statusConfig = PARENT_ACTIVE_STATUS_CONFIG[status as keyof typeof PARENT_ACTIVE_STATUS_CONFIG];
  }

  const sourceConfig = detailRecord.source
    ? (REGISTRATION_SOURCE_CONFIG[detailRecord.source] ?? DEFAULT_SOURCE_CONFIG)
    : null;

  const sections = detailRecord.sections ?? {};

  const StatusIcon = statusConfig?.icon ?? CheckCircle2;

  const tabs: TabItem<ParentDetailTab>[] = PARENT_DETAIL_TAB_CONFIGS.map(
    ({ id, label, icon: Icon }) => ({
      id,
      label,
      icon: <Icon className="w-3 h-3" />,
    }),
  );

  return (
    <>
      <div className="details-view-header">
        <div className="details-view-header-left">
          <div className="details-view-header-icon">
            <User className="w-6 h-6 text-[var(--color-2-nuance-1)]" />
          </div>
          <div className="details-view-header-content">
            <div className="details-view-header-with-badges">
              <h3 className="details-view-header-title">
                {detailRecord.user?.firstName ??
                  (sections['basic_information'] as any)?.firstName ??
                  '—'}{' '}
                {detailRecord.user?.lastName ??
                  (sections['basic_information'] as any)?.lastName ??
                  '—'}
              </h3>
              {statusConfig && (
                <LabelBadge
                  label={statusConfig.label}
                  icon={StatusIcon}
                  variant={statusConfig.badgeVariant}
                  color={statusConfig.badgeColor}
                  size="sm"
                />
              )}
            </div>
            <p className="details-view-header-subtitle">
              {detailRecord.user?.email ??
                (sections['basic_information'] as any)?.email ??
                'Non renseigné'}{' '}
              •{' '}
              {detailRecord.user?.phone ??
                (sections['basic_information'] as any)?.phoneNumber ??
                'Non renseigné'}
            </p>
          </div>
        </div>
      </div>

      <Tabs
        items={tabs}
        activeTab={activeTab}
        onTabChange={(id) => setActiveTab(id as ParentDetailTab)}
      />

      {activeTab === 'details' && (
        <ParentDetailsTab
          record={detailRecord}
          detailRecord={detailRecord}
          statusConfig={statusConfig}
          sourceConfig={sourceConfig}
        />
      )}
      {activeTab === 'registration' && (
        <ParentRegistrationTab
          record={detailRecord}
          sections={sections}
          departments={departments}
          templates={fullState?.templates}
          steps={fullState?.steps}
        />
      )}
      {activeTab === 'children' && (
        <ParentChildrenTab
          record={detailRecord}
          childList={children}
          isLoadingChildren={isLoadingChildren}
          programs={programs}
        />
      )}
      {activeTab === 'history' && (
        <UserHistoryTimeline userId={detailRecord.userId} role="parent" />
      )}
    </>
  );
};

/** Factory for the {@link DataTable} expanded-row collapse. */
export const renderParentDetails = (
  record: ParentRecord,
  fullState?: FullState,
): React.ReactNode => <ParentDetails record={record} fullState={fullState} />;
